import { BreakpointObserver } from '@angular/cdk/layout';
import { Injectable, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';

import { UI_LAYOUT_CONFIG } from './layout.config';

/**
 * Shell layout state, shared by `<ui-app-shell>` and `<ui-sidenav>`.
 * Signal-based so consuming components stay OnPush and template-only.
 *
 * Two independent pieces of state cover every viewport:
 *  - `sidenavCollapsed` — desktop icon-rail state, persisted across sessions.
 *  - `drawerOpen`       — handset overlay drawer, always starts closed.
 */
@Injectable({ providedIn: 'root' })
export class LayoutService {
  private readonly breakpoints = inject(BreakpointObserver);
  private readonly config = inject(UI_LAYOUT_CONFIG);

  /** True on viewports too narrow for a docked sidenav. */
  readonly isHandset = toSignal(
    this.breakpoints.observe(this.config.handsetBreakpoint).pipe(map(({ matches }) => matches)),
    { initialValue: this.breakpoints.isMatched(this.config.handsetBreakpoint) },
  );

  private readonly collapsedState = signal(readStoredCollapsed(this.config.storageKey));
  private readonly drawerOpenState = signal(false);

  /** Desktop-only icon rail state. */
  readonly sidenavCollapsed = this.collapsedState.asReadonly();

  /** Handset-only overlay drawer state. */
  readonly drawerOpen = this.drawerOpenState.asReadonly();

  /** The visual mode the sidenav is currently in, for templates. */
  readonly sidenavMode = computed<'docked' | 'rail' | 'drawer'>(() => {
    if (this.isHandset()) {
      return 'drawer';
    }
    return this.collapsedState() ? 'rail' : 'docked';
  });

  toggleSidenav(): void {
    const collapsed = !this.collapsedState();
    this.collapsedState.set(collapsed);
    persistCollapsed(this.config.storageKey, collapsed);
  }

  openDrawer(): void {
    this.drawerOpenState.set(true);
  }

  closeDrawer(): void {
    this.drawerOpenState.set(false);
  }

  toggleDrawer(): void {
    this.drawerOpenState.update((open) => !open);
  }
}

function readStoredCollapsed(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

function persistCollapsed(key: string, collapsed: boolean): void {
  try {
    localStorage.setItem(key, String(collapsed));
  } catch {
    // Storage unavailable (e.g. private browsing) — state simply lives in memory.
  }
}
